import { useState, useEffect } from "react";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { createLogger } from "@/lib/utils/logger";
import { supabase } from "@/integrations/supabase/client";

const logger = createLogger('auth', 'PositionSelect');

interface PositionSelectProps {
  position: string;
  onChange: (value: string) => void;
  customPosition: string;
  onCustomPositionChange: (value: string) => void;
  disabled?: boolean;
  // Add new optional props for backward compatibility
  value?: string;
  positions?: { value: string; label: string }[];
}

export function PositionSelect({
  position,
  onChange,
  customPosition,
  onCustomPositionChange,
  disabled,
  // Handle alternate prop names for backward compatibility
  value,
  positions: providedPositions,
}: PositionSelectProps) {
  // Use value prop as fallback if position isn't provided
  const actualPosition = position || value || "";
  const [showCustomPosition, setShowCustomPosition] = useState(actualPosition === "Other");
  const [loading, setLoading] = useState(true);
  const [positionsData, setPositionsData] = useState<{ value: string; label: string }[]>([]);

  // Log initial props for debugging
  useEffect(() => {
    logger.info(JSON.stringify({ 
      position,
      value, 
      actualPosition,
      customPosition,
      providedPositionsCount: providedPositions?.length || 0,
      showCustomPosition
    }));
  }, []);

  // Fetch positions from the database
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('positions')
          .select('title, category')
          .order('title');
        
        if (error) {
          logger.info(`Error fetching positions: ${error.message}`);
          return;
        }
        
        if (data && data.length > 0) {
          const formattedPositions = data.map(pos => ({
            value: pos.title,
            label: pos.title,
          }));
          
          // Add "Other" option
          if (!formattedPositions.some(p => p.value === "Other")) {
            formattedPositions.push({ value: "Other", label: "Other (specify)" });
          }
          
          // Remove duplicates by title
          const uniquePositions = Array.from(
            new Map(formattedPositions.map(item => [item.value, item])).values()
          );
          
          setPositionsData(uniquePositions);
          logger.info(JSON.stringify({ 
            fetchedPositionsCount: uniquePositions.length,
            positionsSource: "database" 
          }));
        } else {
          // Fallback if no positions in database
          setPositionsData([
            { value: "Other", label: "Other (specify)" }
          ]);
          logger.info("No positions found in database, using fallback");
        }
      } catch (error) {
        logger.info(`Exception fetching positions: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch positions if not provided via props
    if (!providedPositions || providedPositions.length === 0) {
      fetchPositions();
    } else {
      setPositionsData(providedPositions);
      setLoading(false);
      logger.info(JSON.stringify({ 
        usingProvidedPositions: true,
        positionCount: providedPositions.length,
        positions: providedPositions.map(p => p.label).join(', ')
      }));
    }
  }, [providedPositions]);
  
  useEffect(() => {
    // Update the visibility of the custom position field whenever position changes
    setShowCustomPosition(actualPosition === "Other");
    logger.info(JSON.stringify({ 
      actualPosition, 
      showCustomPosition: actualPosition === "Other" 
    }));
  }, [actualPosition]);

  // Determine final positions to use
  const positions = positionsData.length > 0 ? positionsData : [];

  // Ensure positions includes "Other" option
  const positionsWithOther = positions.some(p => p.value === "Other")
    ? positions
    : [...positions, { value: "Other", label: "Other (specify)" }];

  const handlePositionChange = (value: string) => {
    logger.info(`Position selected: ${value}`);
    onChange(value);
    setShowCustomPosition(value === "Other");
  };

  const handleCustomPositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.info(`Custom position entered: ${e.target.value}`);
    onCustomPositionChange(e.target.value);
  };

  return (
    <div className="space-y-2">
      <Combobox
        options={positionsWithOther}
        value={actualPosition}
        onChange={handlePositionChange}
        placeholder={loading ? "Loading positions..." : "Select your position"}
        emptyMessage={positions.length === 0 ? (loading ? "Loading..." : "No positions found.") : "No results found."}
        searchPlaceholder="Search positions..."
        className="border-accent-light/20 focus-visible:ring-primary/20"
        disabled={disabled || loading}
      />
      {showCustomPosition && (
        <div className="mt-2">
          <Label htmlFor="customPosition" className="font-body">Specify Position</Label>
          <Input
            id="customPosition"
            value={customPosition}
            onChange={handleCustomPositionChange}
            placeholder="Enter your position"
            className="mt-1 border-accent-light/20 focus-visible:ring-primary/20"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
